import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { ApiError, onSessionChange, request, tokens } from "./api";
import type { Role, TokenPair, User } from "./types";

type Session =
  | { status: "loading"; user: null }
  | { status: "signed-out"; user: null }
  | { status: "signed-in"; user: User };

interface Auth {
  session: Session;
  user: User | null;
  signIn(email: string, password: string): Promise<void>;
  signOut(): void;
  /** Roles build on each other: admin > operator > viewer. */
  can(role: Role): boolean;
}

const RANK: Record<Role, number> = { viewer: 0, operator: 1, admin: 2 };
const AuthContext = createContext<Auth | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session>(() =>
    tokens.get() ? { status: "loading", user: null } : { status: "signed-out", user: null },
  );

  const loadUser = useCallback(async () => {
    try {
      const user = await request<User>("/auth/me");
      setSession({ status: "signed-in", user });
    } catch (error) {
      // A server that can't be reached says nothing about the session: keep the
      // tokens and let the user retry. Only a refusal signs them out.
      if (error instanceof ApiError && error.status === 401) tokens.clear();
      setSession({ status: "signed-out", user: null });
    }
  }, []);

  useEffect(() => {
    if (tokens.get()) void loadUser();
  }, [loadUser]);

  // The session can also end without the user doing anything: the refresh token
  // expired, or an admin deactivated the account.
  useEffect(
    () =>
      onSessionChange((signedIn) => {
        if (!signedIn) {
          setSession({ status: "signed-out", user: null });
          queryClient.clear();
        }
      }),
    [queryClient],
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      const pair = await request<TokenPair>("/auth/login", {
        method: "POST",
        body: { email, password },
        authenticated: false,
      });
      tokens.set(pair);
      await loadUser();
    },
    [loadUser],
  );

  const signOut = useCallback(() => tokens.clear(), []);

  const value = useMemo<Auth>(
    () => ({
      session,
      user: session.user,
      signIn,
      signOut,
      can: (role) => session.user !== null && RANK[session.user.role] >= RANK[role],
    }),
    [session, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): Auth {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("useAuth must be used inside <AuthProvider>");
  return auth;
}
